/**
 * MODULE: _shared/frontend-shell/src/components/assistant/AssistantMessageFollowUpChips.tsx
 * PURPOSE: Tap-to-ask follow-up chip row under the latest assistant reply.
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-07-09 [2.2.1493] — **`aria-label`** uses operator chip label (visible **Do it**), not full prompt.
 * RECENT_PRIOR: 2026-07-08 [2.2.1485] — Operator "Do it" primary chip + advisor_execute styling.
 */

import type { AssistantFollowUpChipItem } from '../../hooks/assistant/assistantOperatorMode';

export type AssistantMessageFollowUpChipsProps = {
  /** Structured chips (preferred). */
  chips?: AssistantFollowUpChipItem[];
  /** Plain prompt strings (legacy). */
  prompts?: string[];
  disabled?: boolean;
  onSelect: (prompt: string, chip?: AssistantFollowUpChipItem) => void;
  testId?: string;
};

export function AssistantMessageFollowUpChips({
  chips,
  prompts,
  disabled = false,
  onSelect,
  testId = 'assistant-message-follow-up-chips',
}: AssistantMessageFollowUpChipsProps) {
  const items: AssistantFollowUpChipItem[] =
    chips?.length
      ? chips
      : (prompts ?? []).map((prompt) => ({
          label: prompt,
          prompt,
          executionMode: 'prompt' as const,
        }));

  if (!items.length) return null;

  return (
    <div className="mt-2" data-testid={testId}>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Suggested follow-ups
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((chip) => {
          const isOperator = chip.isOperatorPrimary || chip.executionMode === 'advisor_execute';
          return (
            <button
              key={chip.prompt}
              type="button"
              disabled={disabled}
              aria-label={
                chip.isOperatorPrimary && chip.label !== chip.prompt
                  ? `${chip.label}: ${chip.prompt}`
                  : chip.label || chip.prompt
              }
              title={chip.prompt !== chip.label ? chip.prompt : undefined}
              data-testid="assistant-message-follow-up-chip"
              data-execution-mode={chip.executionMode}
              data-operator-primary={chip.isOperatorPrimary ? 'true' : undefined}
              onClick={() => onSelect(chip.prompt, chip)}
              className={[
                'rounded-full border px-2.5 py-1 text-left text-[11px] font-medium shadow-sm transition disabled:opacity-50',
                isOperator
                  ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700 dark:border-primary-400 dark:bg-primary-500 dark:hover:bg-primary-600'
                  : 'border-gray-200 bg-white text-gray-800 hover:border-primary-300 hover:bg-primary-50/80 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-primary-600 dark:hover:bg-primary-950/30',
              ].join(' ')}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
